import { useMemo } from 'react';
import useGetMarkers from './useGetMarkers';
import { useInfiniteHits } from 'react-instantsearch-hooks-web';

const getHitsByCoord = ( hits ) => {
	if ( ! hits || hits.length === 0 ) {
		return [];
	}
	const hitsByCoord = [];
	for ( const [ key, hit ] of Object.entries( hits ) ) {
		const coordToCheck = hit.neighborhood_data ? hit.neighborhood_data._geoloc : hit._geoloc;
		if ( ! coordToCheck ) {
			continue;
		}

		const exisitingCoord = hitsByCoord.find( ( hitInCoord ) => hitInCoord._geoloc?.lat === coordToCheck?.lat && hitInCoord._geoloc?.lng === coordToCheck?.lng );
		if ( ! exisitingCoord ) {
			hitsByCoord.push( {
				_geoloc: coordToCheck,
				hits: [ hit ],
			} );
			continue;
		}

		exisitingCoord.hits.push( hit );
	}
	return hitsByCoord;
};

const sortHitsByTitle = ( hits ) => {
	return hits.sort( ( a, b ) => {
		const titleA = a.title.toLowerCase();
		const titleB = b.title.toLowerCase();
		if ( titleA < titleB ) {
			return -1;
		}
		if ( titleA > titleB ) {
			return 1;
		}
		return 0;
	} );
};

// show states:
// - if no results from algolia
// - if no state or submarket selected
const useSearchMarkers = () => {
	const hitProps = useInfiniteHits();
	const { hits, currentPageHits } = hitProps;
	const { states } = useGetMarkers();
	const { _state } = hitProps.results;
	const isAlgoliaReady = hitProps.bindEvent !== undefined;

	const hasRefinement = useMemo( () => {
		const pathParts = window.location.pathname.split( '/' );
		const stateSegment = pathParts[ 2 ];
		const submarketSegment = pathParts[ 3 ];

		let hasFacetRefinement = false;
		for ( const [ key, value ] of Object.entries( _state.disjunctiveFacetsRefinements ) ) {
			if ( value.length > 0 ) {
				hasFacetRefinement = true;
			}
		}

		// use algolia state rather than context because it prevents the lag where things don't match up
		return !! stateSegment ||
			!! submarketSegment ||
			_state.filters !== '' ||
			_state.query !== '' ||
			hasFacetRefinement;
	}, [ _state.disjunctiveFacetsRefinements, _state.query, _state.filters ] );

	// if algolia isn't ready, return null to prevent map from displaying
	// otherwise it shows zoomed in middle of the US (not sure why)
	if ( hasRefinement && ! isAlgoliaReady ) {
		return {
			source: 'algolia',
			hits: null,
		};
	}

	// first check for algolia results
	// to prevent flash of states
	if ( hasRefinement && currentPageHits.length > 0 ) {
		return {
			source: 'algolia',
			hits: getHitsByCoord( sortHitsByTitle( hits ) ),
		};
	}

	// show states if no refinements selected or no results from algolia
	return {
		source: 'api',
		hits: states,
	};
};

export default useSearchMarkers;
